'use client'

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '@/components/ui/carousel'
import { Calendar, MapPin, Users, Presentation, MessageSquare, Mic, ArrowRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'

import { FadeUp } from '@/components/ui/fade-up'
interface Slide {
  image: string
  alt: string
  caption: string
}

export function EventReportContent() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [mainApi, setMainApi] = useState<CarouselApi>()
  const [thumbApi, setThumbApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)

  const openingSlides: Slide[] = [
    {
      image: '/images/news/digital_pride_hiroshima_event_report_20251006_speech.webp',
      alt: '代表取締役 進藤史裕による挨拶',
      caption: '代表取締役 進藤史裕による挨拶'
    },
    {
      image: '/images/news/digital_pride_hiroshima_event_report_20251006_guest_speech.webp',
      alt: '来賓による挨拶',
      caption: '阿比留 彩子 様（経済産業省 中国経済産業局 地域経済部次長）による挨拶'
    },
    {
      image: '/images/news/digital_pride_hiroshima_event_report_20251006_video_message.webp',
      alt: 'ビデオメッセージ',
      caption: '保井 俊之 様（武蔵野大学・叡啓大学 教授）によるビデオメッセージ'
    }
  ]

  const onThumbClick = useCallback((index: number) => {
    if (!mainApi) return
    mainApi.scrollTo(index)
  }, [mainApi])

  useEffect(() => {
    if (!mainApi) return

    const onSelect = () => {
      setCurrent(mainApi.selectedScrollSnap())
      thumbApi?.scrollTo(mainApi.selectedScrollSnap())
    }

    mainApi.on('select', onSelect)
    onSelect()

    return () => {
      mainApi.off('select', onSelect)
    }
  }, [mainApi, thumbApi])

  // 自動再生機能
  useEffect(() => {
    if (!mainApi) return

    const autoplay = setInterval(() => {
      mainApi.scrollNext()
    }, 5000)

    return () => {
      clearInterval(autoplay)
    }
  }, [mainApi])
  return (
    <div className="flex flex-col">
      {/* ヒーローセクション */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* 背景画像 */}
        <div className="absolute inset-0">
          <Image
            src="/images/news/digital_pride_hiroshima_event_report_20251006.webp"
            alt="Digital Pride Hiroshima リリースイベント"
            fill
            className="object-cover object-[75%_center] md:object-center"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#292623]/80 via-[#292623]/60 to-transparent"></div>
        </div>

        <div className="container relative z-10 px-4 md:px-8 py-16">
          <div
            className="animate-fade-up max-w-4xl mx-auto"
          >
            <Badge className="mb-8 font-['Noto_Sans_JP',_sans-serif] bg-[#BEB0A8] text-[#292623] text-base px-4 py-2">
              イベントレポート
            </Badge>

            <h1 className="font-['Shippori_Mincho_B1',_serif] text-[32px] md:text-[48px] font-bold text-white mb-6 leading-tight">
              Digital Pride Hiroshima<br />
              リリースイベント開催レポート
            </h1>

            <p className="font-['Noto_Sans_JP',_sans-serif] text-[18px] md:text-[20px] text-white/90 font-medium leading-relaxed">
              2025年10月6日（月）、地域文化を成長力にするDX拠点のリリースイベントを開催しました
            </p>
          </div>
        </div>
      </section>

      {/* イベント概要 */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container px-4">
          <FadeUp
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-['Shippori_Mincho_B1',_serif] text-[28px] md:text-[40px] font-bold text-[#292623] mb-12 text-center">
              イベント概要
            </h2>

            <Card className="border-[#BEB0A8]">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Calendar className="h-6 w-6 text-[#292623] mt-1" />
                    <div>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/60 mb-1">日時</p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[16px] font-medium text-[#292623]">
                        2025年10月6日（月） 15:00〜18:30
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <MapPin className="h-6 w-6 text-[#292623] mt-1" />
                    <div>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/60 mb-1">会場</p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[16px] font-medium text-[#292623] mb-2">
                        Digital Pride Hiroshima
                      </p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[14px] text-[#292623]/70">
                        〒730-0053<br/>
                        広島県広島市中区東千田町1-1-61<br/>
                        hitoto広島ナレッジスクエア 1F
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Users className="h-6 w-6 text-[#292623] mt-1" />
                    <div>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/60 mb-1">参加者</p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-[16px] font-medium text-[#292623]">
                        約100名
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="mt-8 space-y-6 font-['Noto_Sans_JP',_sans-serif] text-[15px] md:text-[16px] text-[#292623]/80 leading-relaxed">
              <p>
                2025年10月6日（月）、地域文化を成長力にするDX拠点「Digital Pride Hiroshima」のリリースイベントが開催されました。
              </p>
              <p>
                当日は、オープニングセレモニー、パネルディスカッション、事例デモンストレーション、懇親会など盛りだくさんの内容で、地域企業、自治体、教育機関など多くの関係者の皆様にご来場いただきました。
              </p>
            </div>

            {/* イベント写真カルーセル */}
            <FadeUp
              className="mt-12"
            >
              <div className="w-full max-w-4xl mx-auto space-y-4">
                {/* メインカルーセル */}
                <Carousel
                  setApi={setMainApi}
                  opts={{
                    align: "start",
                    loop: true,
                  }}
                  className="w-full"
                >
                  <CarouselContent>
                    {openingSlides.map((slide, index) => (
                      <CarouselItem key={index}>
                        <div className="p-1">
                          <button
                            type="button"
                            className="w-full relative aspect-[3/2] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setSelectedImage(slide.image)}
                            aria-label={`${slide.alt}を拡大表示`}
                          >
                            <Image
                              src={slide.image}
                              alt={slide.alt}
                              fill
                              className="object-cover"
                            />
                          </button>
                          <p className="mt-4 text-sm text-[#292623]/60 text-center font-['Noto_Sans_JP',_sans-serif]">
                            {slide.caption}
                          </p>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="hidden md:flex" />
                  <CarouselNext className="hidden md:flex" />
                </Carousel>

                {/* サムネイルカルーセル */}
                <Carousel
                  setApi={setThumbApi}
                  opts={{
                    containScroll: "keepSnaps",
                    dragFree: true,
                  }}
                  className="w-full"
                >
                  <CarouselContent className="flex gap-2 ml-0">
                    {openingSlides.map((slide, index) => (
                      <CarouselItem
                        key={index}
                        className="basis-1/3 pl-0"
                      >
                        <button
                          type="button"
                          className={`w-full relative aspect-[3/2] rounded-md overflow-hidden cursor-pointer transition-all ${
                            index === current
                              ? 'ring-2 ring-[#292623] ring-offset-2 opacity-100'
                              : 'opacity-50 hover:opacity-75'
                          }`}
                          onClick={() => onThumbClick(index)}
                          aria-label={`${slide.alt}を選択`}
                        >
                          <Image
                            src={slide.image}
                            alt={slide.alt}
                            fill
                            className="object-cover"
                          />
                        </button>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              </div>
            </FadeUp>
          </FadeUp>
        </div>
      </section>

      {/* パネルディスカッション */}
      <section className="py-16 md:py-24 bg-[#BEB0A8]/20">
        <div className="container px-4">
          <FadeUp
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-[#292623] text-white">プログラム</Badge>
              <h2 className="font-['Shippori_Mincho_B1',_serif] text-[28px] md:text-[40px] font-bold text-[#292623]">
                パネルディスカッション
              </h2>
            </div>

            <div className="space-y-8">
              {/* パネルディスカッション① */}
              <FadeUp
              >
                <Card className="bg-white border-[#BEB0A8] hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="p-3 bg-[#BEB0A8]/20 rounded-lg">
                        <MessageSquare className="h-6 w-6 text-[#292623]" />
                      </div>
                      <div className="flex-1">
                        <Badge className="mb-2">パネルディスカッション①</Badge>
                        <h3 className="font-['Noto_Sans_JP',_sans-serif] text-[18px] md:text-[20px] font-bold text-[#292623] mb-4">
                          「破壊か、成長か？デジタル化は地域文化をどう変える？」
                        </h3>
                      </div>
                    </div>

                    <p className="font-['Noto_Sans_JP',_sans-serif] text-[14px] md:text-[15px] text-[#292623]/70 leading-relaxed mb-6">
                      第1部のパネルディスカッションでは、「破壊か、成長か？デジタル化は地域文化をどう変える？」というテーマで、地域のDX推進における文化の重要性について議論が交わされました。
                    </p>

                    <div className="bg-[#BEB0A8]/10 p-4 rounded-lg mb-6">
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/80 mb-2">
                        <strong>登壇者：</strong>
                      </p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/70 leading-relaxed">
                        モデレーター：丸本 健二郎 氏（オフターク合同会社 代表社員）<br/>
                        パネラー：星山 雄史 氏（株式会社エル・ティー・エス 執行役員）、菊地 雄太 氏（中国経済産業局 デジタル経済課係長）、谷口 千春 氏（株式会社DoTS 代表取締役兼役社長）、進藤 史裕（株式会社CodeFox 代表取締役）
                      </p>
                    </div>

                    <p className="text-[14px] text-[#292623]/70 leading-relaxed">
                      ディスカッションでは、グローバルプラットフォームによる文化の画一化への懸念や、文化を伝統に昇華させるクリエイターの必要性、ChatGPTに代表される技術革新による自律への転換期、そして地域のハブ機能とコミュニティの重要性について意見が交わされました。
                    </p>

                    {/* パネルディスカッション①の写真 */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                      {[1, 2, 3, 4].map((num) => (
                        <button
                          key={num}
                          type="button"
                          className="relative aspect-[3/2] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setSelectedImage(`/images/news/digital_pride_hiroshima_event_report_20251006_panel1_0${num}.webp`)}
                          aria-label={`パネルディスカッション① ${num}を拡大表示`}
                        >
                          <Image
                            src={`/images/news/digital_pride_hiroshima_event_report_20251006_panel1_0${num}.webp`}
                            alt={`パネルディスカッション① ${num}`}
                            fill
                            className="object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </FadeUp>

              {/* パネルディスカッション② */}
              <FadeUp delay={0.1}
              >
                <Card className="bg-white border-[#BEB0A8] hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="p-3 bg-[#BEB0A8]/20 rounded-lg">
                        <MessageSquare className="h-6 w-6 text-[#292623]" />
                      </div>
                      <div className="flex-1">
                        <Badge className="mb-2">パネルディスカッション②</Badge>
                        <h3 className="font-['Noto_Sans_JP',_sans-serif] text-[18px] md:text-[20px] font-bold text-[#292623] mb-4">
                          「製造業におけるDXの現在地と可能性」
                        </h3>
                      </div>
                    </div>

                    <p className="font-['Noto_Sans_JP',_sans-serif] text-[14px] md:text-[15px] text-[#292623]/70 leading-relaxed mb-6">
                      第2部では、製造業に特化したDXの現状と今後の展望について、実務者の視点から活発な意見交換が行われました。
                    </p>

                    <div className="bg-[#BEB0A8]/10 p-4 rounded-lg mb-6">
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/80 mb-2">
                        <strong>登壇者：</strong>
                      </p>
                      <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/70 leading-relaxed">
                        モデレーター：平岡 良介 氏（平岡工業株式会社 代表取締役兼役社長）<br/>
                        パネラー：桑原 弘明 氏（株式会社TD Holdings 代表取締役）、岩村 孝浩 氏（株式会社TD Holdings）、進藤 史裕（株式会社CodeFox 代表取締役）
                      </p>
                    </div>

                    <p className="text-[14px] text-[#292623]/70 leading-relaxed">
                      ディスカッションでは、製造業におけるDX人財の育成や出向研修の取り組み、AIコーディング技術を活用したフルスクラッチ開発の可能性、そして開発ハードルの低下により求められる完成スピードと質、さらにはAI時代における基礎知識の重要性について議論が展開されました。
                    </p>

                    {/* パネルディスカッション②の写真 */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[1, 2, 3, 4].map((num) => (
                        <button
                          key={num}
                          type="button"
                          className="relative aspect-[3/2] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setSelectedImage(`/images/news/digital_pride_hiroshima_event_report_20251006_panel2_0${num}.webp`)}
                          aria-label={`パネルディスカッション② ${num}を拡大表示`}
                        >
                          <Image
                            src={`/images/news/digital_pride_hiroshima_event_report_20251006_panel2_0${num}.webp`}
                            alt={`パネルディスカッション② ${num}`}
                            fill
                            className="object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </FadeUp>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* 事例デモンストレーション */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container px-4">
          <FadeUp
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Presentation className="h-6 w-6 text-[#292623]" />
                <Badge className="bg-[#292623] text-white">デモンストレーション</Badge>
              </div>
              <h2 className="font-['Shippori_Mincho_B1',_serif] text-[28px] md:text-[40px] font-bold text-[#292623]">
                事例デモンストレーション
              </h2>
            </div>

            <div className="space-y-6 font-['Noto_Sans_JP',_sans-serif] text-[15px] md:text-[16px] text-[#292623]/80 leading-relaxed">
              <p>
                イベント会場では、Digital Pride Hiroshimaで開発・実証中の各種システムのデモンストレーションも実施されました。
              </p>

              <Card className="border-[#BEB0A8]">
                <CardContent className="p-6">
                  <h3 className="font-bold text-[#292623] mb-4">展示内容</h3>
                  <ul className="space-y-2 text-[14px] md:text-[15px] text-[#292623]/70">
                    <li className="flex items-start gap-2">
                      <span className="text-[#BEB0A8] mt-1">●</span>
                      <span>地域DAOシステム</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#BEB0A8] mt-1">●</span>
                      <span>在庫管理システム</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#BEB0A8] mt-1">●</span>
                      <span>ピースメッセージ（CRM）</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#BEB0A8] mt-1">●</span>
                      <span>Web制作事例</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#BEB0A8] mt-1">●</span>
                      <span>その他開発中のプロダクト</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* 懇親会・ネットワーキング */}
      <section className="py-16 md:py-24 bg-[#BEB0A8]/20">
        <div className="container px-4">
          <FadeUp
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Users className="h-6 w-6 text-[#292623]" />
                <Badge className="bg-[#292623] text-white">ネットワーキング</Badge>
              </div>
              <h2 className="font-['Shippori_Mincho_B1',_serif] text-[28px] md:text-[40px] font-bold text-[#292623]">
                懇親会・ネットワーキング
              </h2>
            </div>

            <div className="space-y-6 font-['Noto_Sans_JP',_sans-serif] text-[15px] md:text-[16px] text-[#292623]/80 leading-relaxed">
              <p>
                16:30からは、Digital Pride Hiroshimaに隣接する「いいオフィス広島」にて懇親会を開催。
              </p>

              {/* 懇親会の写真 */}
              <FadeUp
                className="my-8"
              >
                <button
                  type="button"
                  className="w-full relative aspect-[3/2] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setSelectedImage('/images/news/digital_pride_hiroshima_event_report_20251006_networking.webp')}
                  aria-label="懇親会での挨拶を拡大表示"
                >
                  <Image
                    src="/images/news/digital_pride_hiroshima_event_report_20251006_networking.webp"
                    alt="懇親会での挨拶"
                    fill
                    className="object-cover"
                  />
                </button>
                <p className="mt-4 text-sm text-[#292623]/60 text-center font-['Noto_Sans_JP',_sans-serif]">
                  桑原 義幸 様（株式会社Trive 常務執行役員社長補佐 兼 CTO）による懇親会挨拶
                </p>
              </FadeUp>

              {/* 縁側ピッチ */}
              <div className="mt-12">
                <div className="flex items-center gap-2 mb-6">
                  <Mic className="h-5 w-5 text-[#292623]" />
                  <h3 className="font-['Noto_Sans_JP',_sans-serif] text-[20px] md:text-[24px] font-bold text-[#292623]">
                    縁側ピッチ
                  </h3>
                </div>

                <p className="mb-6">
                  懇親会では、地域企業や支援者の皆様による「縁側ピッチ」も行われました。各社の取り組みや今後の展望について、熱のこもったプレゼンテーションが繰り広げられました。
                </p>

                {/* 縁側ピッチの写真 */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <FadeUp delay={num * 0.05}
                      key={num}
                    >
                      <button
                        type="button"
                        className="w-full relative aspect-[3/2] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage(`/images/news/digital_pride_hiroshima_event_report_20251006_engawa_pitch_0${num}.webp`)}
                        aria-label={`縁側ピッチ ${num}を拡大表示`}
                      >
                        <Image
                          src={`/images/news/digital_pride_hiroshima_event_report_20251006_engawa_pitch_0${num}.webp`}
                          alt={`縁側ピッチ ${num}`}
                          fill
                          className="object-cover"
                        />
                      </button>
                    </FadeUp>
                  ))}
                </div>
              </div>

              {/* 締めの挨拶 */}
              <div className="mt-16">
                <h3 className="font-['Noto_Sans_JP',_sans-serif] text-[20px] md:text-[24px] font-bold text-[#292623] mb-6">
                  締めの挨拶
                </h3>

                <FadeUp
                >
                  <button
                    type="button"
                    className="w-full relative aspect-[3/2] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setSelectedImage('/images/news/digital_pride_hiroshima_event_report_20251006_closing.webp')}
                    aria-label="締めの挨拶を拡大表示"
                  >
                    <Image
                      src="/images/news/digital_pride_hiroshima_event_report_20251006_closing.webp"
                      alt="締めの挨拶"
                      fill
                      className="object-cover"
                    />
                  </button>
                  <p className="mt-4 text-sm text-[#292623]/60 text-center font-['Noto_Sans_JP',_sans-serif]">
                    佐々木 雅弘 様（一般社団法人禎子の折り鶴 代表理事）による締めの挨拶
                  </p>
                </FadeUp>
              </div>

              {/* 記念品贈呈 */}
              <div className="mt-16">
                <h3 className="font-['Noto_Sans_JP',_sans-serif] text-[20px] md:text-[24px] font-bold text-[#292623] mb-6">
                  記念品贈呈
                </h3>

                <p className="font-['Noto_Sans_JP',_sans-serif] text-[14px] md:text-[15px] text-[#292623]/70 leading-relaxed mb-6">
                  締めの挨拶に続き、佐々木 雅弘 様から進藤へ、株式会社キャステム（広島県福山市）が精密鋳造技術と最新のデジタル技術を掛け合わせて金属で再現した、佐々木禎子さんの折り鶴
                  <a
                    href="https://www.castem.co.jp/project/detail?id=2616"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#292623] font-bold hover:underline"
                  >
                    「SADAKO」
                  </a>
                  が贈呈されました。
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[1, 2, 3].map((num) => (
                    <FadeUp delay={num * 0.05}
                      key={num}
                    >
                      <button
                        type="button"
                        className="w-full relative aspect-[3/2] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage(`/images/news/digital_pride_hiroshima_event_report_20251006_presentation_0${num}.webp`)}
                        aria-label={`記念品贈呈 ${num}を拡大表示`}
                      >
                        <Image
                          src={`/images/news/digital_pride_hiroshima_event_report_20251006_presentation_0${num}.webp`}
                          alt={`記念品贈呈 ${num}`}
                          fill
                          className="object-cover"
                        />
                      </button>
                    </FadeUp>
                  ))}
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Digital Pride Hiroshimaについて */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container px-4">
          <FadeUp
            className="max-w-4xl mx-auto text-center"
          >
            <p className="font-['Noto_Sans_JP',_sans-serif] text-[15px] md:text-[16px] text-[#292623]/80 mb-6">
              Digital Pride Hiroshimaについて、詳しくはこちらをご覧ください。
            </p>
            <Button asChild size="lg" className="bg-[#292623] hover:bg-[#292623]/90 w-full md:w-auto">
              <Link href="/digital-pride" className="flex items-center justify-center">
                Digital Pride Hiroshimaについて
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </FadeUp>
        </div>
      </section>

      {/* 画像拡大表示ダイアログ */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-7xl w-full p-0 bg-transparent border-none">
          <DialogTitle className="sr-only">画像拡大表示</DialogTitle>
          {selectedImage && (
            <div className="relative w-full h-[80vh]">
              <Image
                src={selectedImage}
                alt="拡大画像"
                fill
                className="object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
